import { parse } from 'qs';
import React, { type ReactElement, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { PROMO_TARIFFS } from 'reactApp/appHelpers/configHelpers';
import { autouploadPromoBlockMobileEnabled } from 'reactApp/appHelpers/featuresHelpers/features/autoUploadPromo';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { useBuyClick } from 'reactApp/sections/QuotaLanding/TariffsPlans/TariffPlansCard/useBuyClick';
import type { RootState } from 'reactApp/store';
import { PromoBlock } from 'reactApp/ui/Mobile/PromoBlock/PromoBlock';
import { PromoBlockData } from 'reactApp/ui/Mobile/PromoBlock/PromoBlock.data';
import { sendSplashScreenAnalytics } from 'reactApp/ui/Mobile/SplashScreen/SplashScreen.analytics';
import { openUnlimitedUploadInfo } from 'reactApp/ui/Mobile/UnlimitedUploadInfo/UnlimitedUploadInfo.helpers';

type props = {
    isMobilePayAvailable: boolean;
    isIOSPromo: boolean;
    isMobilePayFromGetParam?: boolean;
    isAndroidPromo?: boolean;
    mayPromo2025Enabled?: boolean;
};

export const useAutoUploadPromoBlock = ({
    isMobilePayAvailable,
    isIOSPromo,
    isMobilePayFromGetParam = false,
    isAndroidPromo = false,
    mayPromo2025Enabled,
}: props): ReactElement | null => {
    const isPaidUser = useSelector(UserSelectors.isPaidUser);
    const { ref_id } = parse(window.location.search, { ignoreQueryPrefix: true }) || {};

    const showAutouploadPromoBlock = !isPaidUser && autouploadPromoBlockMobileEnabled && !ref_id;

    const product = useSelector((state: RootState) => ProductsSelectors.getProductById(state, PROMO_TARIFFS.autouploadPromoBlockLanding));
    const { isLoaded } = useSelector(ProductsSelectors.getLifeCycleState);

    const sendAnalytics = (action) => sendSplashScreenAnalytics({ action, name: 'unlimited_autoupload_description' });

    const onBuyClick = useBuyClick({
        isMobilePayAvailable,
        isMobilePayFromGetParam,
        isIOSPromo,
        isAndroidPromo,
        enabledMidasPayment: true,
    });

    const handleClick = useCallback(() => {
        sendAnalytics('click');
        if (product) {
            onBuyClick({ product, paySource: 'autoupload_info_block', space: product.space.space });
        }
    }, [product, onBuyClick]);

    const handleSecondaryClick = useCallback(() => {
        sendAnalytics('click-details');
        openUnlimitedUploadInfo();
    }, []);

    if (mayPromo2025Enabled) {
        return null;
    }

    if (showAutouploadPromoBlock) {
        return (
            <PromoBlock
                {...PromoBlockData.AUTOUPLOAD_ON_LANDING}
                onClick={handleClick}
                onSecondaryClick={handleSecondaryClick}
                buttonDisabled={!isLoaded}
            />
        );
    }

    return null;
};
